import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field, formValueSelector } from 'redux-form';
import { get, isEmpty } from 'lodash';
import { injectIntl, FormattedMessage, intlShape, FormattedDate } from 'react-intl';
import { getShowBillingForm, priceBreakdownObject, verifyPSCredits } from './forms/bookingUtil';

import ReviewSpaceRentalTable from '../elements-tables/review-space-rental-table';
import { CancellationPolicySection } from './cancellation-policy-section';
import { IdentityVerification } from '../elements-forms/identity-verification';
import Button from '../elements-buttons/Button';
import ApplyCreditField from '../app-main/components/reduxForms/fields/ApplyCreditField/ApplyCreditField';
import TextAreaField from 'shared-components/reduxForms/fields/TextAreaField/TextAreaField';
import CheckboxFieldPanel from 'shared-components/reduxForms/utilities/CheckboxFieldPanel';
import PriceTable from '../elements-tables/price-table';
import AddOnsTable from '../app-main/components/reduxForms/fieldSets/AddOnsTable/add-ons-table';
import { TosCTAdisclaimer } from 'shared-components/terms/cta-disclaimer';
import HostRulesAgree from './HostRulesAgree';
import SocialEventPolicyCheckbox from './SocialEventPolicyCheckbox';
import { connect } from 'react-redux';
import { fetchEnvSpaces } from '../app-main/actions/env';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import SplitPayments from './SplitPayments';
import moment from 'moment';
import { BOOKING_REQUEST_FORM } from './forms/constants';
import SvgCheckSmall from '../shared-components/icons/CheckSmall';
import { TaxesBillingAddress } from './TaxesBillingAddress';
import PaymentSelector from '../elements-stripe/PaymentSelector';
import { withReduxForms, withStripeElements } from '../elements-stripe/utils';
import { CheckoutButton } from '../elements-stripe/CheckoutButton';

const PaymentSelectorField = withReduxForms(PaymentSelector);

class RequestReview extends Component {
  constructor(props) {
    super(props)

    this.isIDVInViewport = this.isIDVInViewport.bind(this);
    this.canExpressCheckout = this.canExpressCheckout.bind(this);

    this.state = {
      has_seen_verification: false,
      has_clicked_verification: false
    };
  }

  componentDidMount () {
    const {
      booking,
      listing,
      publicAddOns,
      dispatch,
      guestNeedsVerification,
      initialValues,
      alcoholConsumed,
      schedule,
      riskScore,
      masterContract,
      spaceRentalContract
    } = this.props;
    const { has_seen_verification, has_clicked_verification } = this.state;
    window.scrollTo(0, 0);

    dispatch(fetchEnvSpaces());

    const socialEventPolicyAgreementRequired = get(
      spaceRentalContract,
      'meta.risk.guest_social_events_policy_agreement_required',
      false
    );

    const contracts = [
      get(booking, 'booking.master', {}),
      ...get(booking, 'booking.children', [])
    ];

    analyticsIntegrations.bkgReqReviewStepViewed(
      contracts,
      listing,
      publicAddOns,
      {
        guest_social_events_policy_agreement_required: socialEventPolicyAgreementRequired
      }
    );

    if (guestNeedsVerification) {
      document.addEventListener('scroll', this.isIDVInViewport); // can add listener here because IDV has rendered in this case
      const analytics_payload = {
        viewed: has_seen_verification,
        clicked: has_clicked_verification,
        booking_request_review: true,
        custom_offer_review: false,
        contracts,
        listingObj: listing,
        publicAddOns,
        alcohol_consumed: alcoholConsumed,
        schedule,
        riskScore
      }
      analyticsIntegrations.reviewIdentityVerification(analytics_payload);
    }
  };

  componentDidUpdate (prevProps, prevState) {
    const { booking, listing, publicAddOns, spaceRentalContract, masterContract, guestNeedsVerification, alcoholConsumed, schedule, riskScore, guestSocialEventsPolicyAccepted } = this.props;
    const { has_seen_verification, has_clicked_verification } = this.state;
    const hasAnalytics = 'object' === typeof analyticsIntegrations;
    const needsVerificationAnalytics = has_seen_verification !== prevState.has_seen_verification || has_clicked_verification !== prevState.has_clicked_verification;
    const { guestSocialEventsPolicyAccepted: prevGuestSocialEventsPolicyAccepted } = prevProps;

    const contracts = [
      get(booking, 'booking.master', {}),
      ...get(booking, 'booking.children', [])
    ];

    const socialEventPolicyAgreementRequired = get(
      spaceRentalContract,
      'meta.risk.guest_social_events_policy_agreement_required',
      false
    );

    if ((!prevGuestSocialEventsPolicyAccepted && guestSocialEventsPolicyAccepted) && socialEventPolicyAgreementRequired) {
      analyticsIntegrations.guestSocialEventPolicyCheckedYes(masterContract, spaceRentalContract, schedule, riskScore);
    }

    if (hasAnalytics && guestNeedsVerification && needsVerificationAnalytics) {
      const analytics_payload = {
        viewed: has_seen_verification,
        clicked: has_clicked_verification,
        booking_request_review: true,
        custom_offer_review: false,
        contracts,
        listingObj: listing,
        publicAddOns,
        alcohol_consumed: alcoholConsumed,
        schedule,
        riskScore
      }
      analyticsIntegrations.reviewIdentityVerification(analytics_payload);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.isIDVInViewport); // remove event listener regardless when they leave page
  };

  isIDVInViewport() { // gets called on scroll until the component state is set { has_seen_verification: true }
    const el = document.getElementById('identity_verification_container'); // selects dom element for container of the IDV header, description, button, errors, etc.
    const rect = el.getBoundingClientRect(); // returns essentially coordinates from top-left of viewport to selected element
    const isVisible = rect.top >= 0 && // top of element is below top edge of viewport
                      rect.left >= 0 && // left of element is right of left edge of viewport
                      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && // bottom is above bottom edge
                      rect.right <= (window.innerWidth || document.documentElement.clientWidth) // right is left of right edge

    if (isVisible) {
      this.setState({
        has_seen_verification: true  // trigger analytics event on state change saying user has seen the verification field
      }, () => document.removeEventListener('scroll', this.isIDVInViewport)); // remove event listener so this fn is no longer called
    }
  };

  // This triggers form validation before Google/Apple Pay
  // Has to return boolean if form is valid
  canExpressCheckout () {
    const { invalid, handleSubmit } = this.props;

    if (invalid) {
      handleSubmit();
    }

    return !invalid;
  }

  render () {
    const {
      handlePaymentChange,
      spaceRentalContract = {},
      isInstaBookingWithValidAttendees = false,
      spaceRentalBooking = {},
      isSubmitting = false,
      selectedPayment,
      paymentInfo,
      normalizedContracts,
      handleClickBack = null,
      ownerAvatarUrl,
      ownerFirstName = '',
      ssoId = '',
      masterContract = {},
      toggleModal = null,
      whichModal = null,
      listingData = {},
      credits = {},
      handleApplyCreditChange = null,
      applyCreditProcessing,
      socialEventPolicyAgreementRequired,
      socialEventsPolicyCopy = [],
      cancellationPolicies = [],
      listing = {},
      accessToken = null,
      guestNeedsVerification = false,
      getIdentityValue = null,
      alcoholConsumed,
      feeProfiles,
      initialValues,
      schedule,
      splitPaymentChange,
      selectedPaymentOption,
      booking,
      splitPaymentProcessing,
      allCountries,
      billingCountryValue,
      guestSpaceCountry,
      handleBillingCountryChange = null,
      handleBillingSubRegionChange = null,
      selectedWallet,
      intl
    } = this.props;

    const billingAddress = get(feeProfiles, 'billingAddress.data', []);
    const displayBillingDropdown = getShowBillingForm(billingAddress);

    const bookingCreatedDate = get(normalizedContracts, 'bookingObject.date_created', '');
    const splitPaymentChargeDate = get(normalizedContracts, 'masterContract.split_payment_charge_date', '');
    const displaySplitPayments = get(normalizedContracts, 'masterContract.split_payment_checkout', '');

    const cancellationPolicy = get(spaceRentalBooking, 'cancellation_policy', {});
    const cancellationPolicyData = _.find(cancellationPolicies, policyData => policyData.id === cancellationPolicy.id) || {};
    const cancellationPolicyDesc = cancellationPolicyData.short_description || cancellationPolicyData.description;

    const priceBreakdown = priceBreakdownObject(normalizedContracts);
    const creditBalance = get(credits, 'balance', 0);
    const psCreditsAllowed = verifyPSCredits(listing);

    const productionDetails = get(listingData, 'production_details', {});
    const isProductionListing = 'Production' === get(listingData, 'use_type');
    const hostRulesData = {
      listingRules: get(listingData, 'rules', ''),
      min_age: get(listingData, 'min_age', ''),
      ...(isProductionListing && productionDetails),
      surveillanceData: get(listingData, 'surveillance_info', {})
    };

    const onIDVClick = (e) => {
      if (!this.state.has_clicked_verification) {
        this.setState({ has_clicked_verification: true })
      }
    }

    const {
      spaceRentalSubtotal,
      spaceRentalTotal,
      discounts,
      itemsArray,
      addOnsTotal,
      cleaningFee,
      currency,
      total,
      guestFee,
      guestPayment,
      creditsApplied,
      remainingGuestTotal,
      guestSplitPayment,
      guestSplitPaymentWithoutFee,
      guestSplitPaymentFee,
      guestSplitPaymentTotal
    } = priceBreakdown;
    const startDate = get(schedule, '[0].start.date', {});
    const bookingDate = moment(`${startDate.y}-${startDate.m}-${startDate.d}`, 'YYYY-MM-DD');
    const displayAddOns = 0 < addOnsTotal;

    const { metro, country } = listingData;
    const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();

    return (
      isEmpty(spaceRentalContract) ? null
        : <div className="RequestReview">
          <ReviewSpaceRentalTable
            discounts={discounts}
            cleaningFee={cleaningFee || 0}
            currency={currency}
            subtotal={spaceRentalSubtotal}
            total={spaceRentalTotal}
            masterContract={masterContract}
          />

          {
            !isEmpty(itemsArray) &&
          <AddOnsTable
            itemsArray={itemsArray}
            ssoId={ssoId}
            currency={currency}
            masterContract={masterContract}
            toggleModal={toggleModal}
            whichModal={whichModal}
            guestView
            isReviewPage
          />
          }

          <PriceTable
            currency={currency}
            spaceRentalTotal={spaceRentalTotal}
            addOnsTotal={addOnsTotal}
            total={total[ssoId]}
            toggleModal={toggleModal}
            whichModal={whichModal}
            processingFee={guestFee}
            priceTotal={guestPayment}
            isHost={false}
            discounts={discounts}
            itemsArray={itemsArray}
            displayAddOns={displayAddOns}
            creditsApplied={selectedPaymentOption === 'pay_partial' ? 0 : creditsApplied}
            remainingGuestTotal={remainingGuestTotal}
            splitPaymentFee={guestSplitPaymentFee}
            splitPaymentAmount={guestSplitPayment}
            splitPaymentAmountWithoutFee={guestSplitPaymentWithoutFee}
            splitPaymentTotal={guestSplitPaymentTotal}
            displaySplitPayments={displaySplitPayments}
            splitPaymentChargeDate={splitPaymentChargeDate}
            bookingCreatedDate={bookingCreatedDate}
            masterContract={masterContract}
            bookingStartDate={<FormattedDate
              value={bookingDate}
              year="numeric"
              month="short"
              day="numeric"
              weekday="short"
            />}
          />

          <hr className="margin-xl-top margin-xl-bottom" />

          { (!!creditBalance && psCreditsAllowed) &&
            <div className="apply-credit-section margin-xl-top margin-xl-bottom">
              <div className="heavy margin-sm-bottom">
                <FormattedMessage id="booking_request.review_request.credits" defaultMessage="Peerspace Credits" />
              </div>
              <Field
                name="apply_credit"
                component={ApplyCreditField}
                currency={currency}
                creditBalance={creditBalance}
                onChange={handleApplyCreditChange}
                isProcessing={applyCreditProcessing}
                creditsApplied={creditsApplied}
                partialPayMethod={selectedPaymentOption === 'pay_partial'}
              />
              <hr className="margin-xl-top margin-xl-bottom" />
            </div>
          }

          <CancellationPolicySection
            policyName={cancellationPolicyData.name}
            policyDescription={cancellationPolicyDesc}
            gracePeriodApplies={cancellationPolicy.grace_period_applies}
          />

          <hr className="margin-xl-top" style={{ marginBottom: 40 }} />

          { socialEventPolicyAgreementRequired &&
            <SocialEventPolicyCheckbox socialEventsPolicyCopy={socialEventsPolicyCopy} />
          }

          <div>
            <HostRulesAgree hostRulesData={hostRulesData} />
            <div className="checkbox-field-panel">
              <Field
                id="accept-rules"
                name="are_host_rules_accepted"
                attributes={{ 'data-testing-id': 'host-rules-checkbox' }}
                component={CheckboxFieldPanel}
                label={ this.props.intl.formatMessage({ id: 'booking_request.review_request.accept_rules', defaultMessage: 'I accept and understand these host rules and disclosures.' }) }
                option={true}
              />
            </div>
          </div>

          {displayBillingDropdown && (
            <>
              <hr className="large-hr-margin" style={{ marginBottom: 28 }} />
              <div className="flex column billing-section margin-xl-top">
                <TaxesBillingAddress
                  intl={intl}
                  feeProfiles={feeProfiles}
                  allCountries={allCountries}
                  defaultCountry={guestSpaceCountry}
                  billingCountryValue={billingCountryValue}
                  onChangeFn={handleBillingCountryChange}
                  onSubRegionChange={handleBillingSubRegionChange}
                />
              </div>
            </>
          )}

          { (paymentInfo && 0 !== remainingGuestTotal) && <hr />}

          { (paymentInfo && 0 !== remainingGuestTotal) &&
          <div className="payment-section">
            <div className="flex middle h6">
              <img className="icon margin-md-right" src="https://assets.peerspace.com/credit-card.svg?v=rb" alt="Credit Card Icon" />
              <span>
              <FormattedMessage id="booking_request.review_request.payment" defaultMessage="Payment" />
            </span>
            </div>
            {
               booking?.booking?.split_payment_permitted &&
              <SplitPayments
                intl={this.props.intl}
                initialValues={initialValues}
                fullAmount={guestPayment}
                partialAmount={guestSplitPayment}
                partialAmountWithoutFee={guestSplitPaymentWithoutFee}
                feeAmount={guestSplitPaymentFee}
                currency={currency}
                isProcessing={splitPaymentProcessing}
                splitPaymentChargeDate={splitPaymentChargeDate}
                onSplitPaymentChange={(value) => {
                  splitPaymentChange(value, priceBreakdown, listingData, normalizedContracts);
                }}
                splitPaymentCheckout={masterContract?.split_payment_checkout}
              />
            }

            <div className="booking-payment">
              <div className="heavier margin-sm-bottom">
                <FormattedMessage id="booking_request.review_request.credit_card" defaultMessage="Credit Card" />
              </div>
              <Field
                name={selectedPayment || (selectedWallet && selectedWallet !== 'card') ? 'payment' : 'cardholders_name'}
                component={PaymentSelectorField}
                cards={paymentInfo}
                selectedCard={selectedPayment}
                setSelectedCard={handlePaymentChange}
              />
            </div>
          </div>
          }

          <div className="disclaimer-block" style={{ margin: '32px 0' }}>
            <p className='flex'>
              <span className='text-green'>
                <SvgCheckSmall width='1.5em' height='1.5em' />
              </span>
              <span className='flex-one padding-lg-left'>
                <FormattedMessage
                  defaultMessage='By entering your card details, you consent to the {feesOverviewLink} and authorize Peerspace, Inc. to charge to this payment method all applicable fees, including any outstanding additional or incidental charges related to any booking linked to your account.'
                  id='booking_request.review_request.card_disclaimer'
                  values={{
                    feesOverviewLink: <a href="/legal/terms/fees-overview">
                      <FormattedMessage defaultMessage="Peerspace Fees Overview" id='booking_request.review_request.fees_overview' />
                    </a>
                  }}
                />
              </span>
            </p>
            <p className='flex'>
              <span className='text-green'>
                <SvgCheckSmall width='1.5em' height='1.5em' />
              </span>
              <span className='flex-one padding-lg-left'>
                <FormattedMessage
                  defaultMessage="Free cancellation and full refund within 24 hours of booking confirmation."
                  id="booking_request.review_request.cancellation"
                />
              </span>
            </p>
            {isInstaBookingWithValidAttendees ? (
              <p className='flex'>
                <span className='text-green'>
                  <SvgCheckSmall width='1.5em' height='1.5em' />
                </span>
                <span className='flex-one padding-lg-left'>
                  <FormattedMessage
                    defaultMessage="By clicking Confirm and Pay, your booking will be instantly confirmed and your card on file will be charged."
                    id="booking_request.review_request.instant_confirmation"
                  />
                </span>
              </p>
            ) : (
              <p className='flex'>
                <span className='text-green'>
                  <SvgCheckSmall width='1.5em' height='1.5em' />
                </span>
                <span className='flex-one padding-lg-left'>
                  <FormattedMessage
                    defaultMessage="By clicking Request to book, you are requesting to book {name}’s space and a hold will be placed on your card. If {name} accepts, your booking will be confirmed and your card will be charged."
                    id="booking_request.review_request.confirmation"
                    values={{
                      name: listing.ownerFirstName
                    }}
                  />
                </span>
              </p>
            )}
          </div>

          { guestNeedsVerification && <hr style={{ marginBottom: '32px' }} />}

          { guestNeedsVerification &&
            <div className="identitify-verification-section margin-xl-bottom" id="identity_verification_container">
              <div className="flex space-between margin-lg-bottom">
                <div>
                  <div className="flex middle h6 no-top-margin">
                    <img className="icon margin-md-right" src="https://assets.peerspace.com/verified-outline.svg?v=rb" alt="Verification Icon" />
                    <FormattedMessage id="booking_request.review_request.verify" defaultMessage="Verify your identity" />
                  </div>
                  <div className="help-block no-top-margin no-bottom-margin text-base">
                    <FormattedMessage
                      id="booking_request.review_request.trust_safety_message"
                      defaultMessage="Trust and safety is our top priority and to keep our community safe all new Peerspace customers must verify their identity. We’ve partnered with Stripe for a seamless verification experience.  Identity verification must be completed on the Peerspace account holder."
                    />
                  </div>
                  <a target="_blank" href={`https://support.peerspace.com/hc/${zendeskLocale}/articles/5141300838420-What-s-needed-to-successfully-complete-identity-verification-`}>
                    <FormattedMessage id="booking_request.review_request.verify_learn_more" defaultMessage="Learn more" />
                  </a>
                  <div className="margin-lg-top">
                    <Field
                      id="identity_verification"
                      name="identity_verification"
                      component={IdentityVerification}
                      ssoId={ssoId}
                      accessToken={accessToken}
                      triggerId={listing._id}
                      triggerType="listing"
                      getIdentityValue={getIdentityValue}
                      alcoholConsumed={alcoholConsumed}
                      analyticsOnClick={onIDVClick}
                      schedule={schedule}
                      metro={metro}
                      country={country}
                    />
                  </div>
                </div>
              </div>
            </div>
          }

          <hr className="large-hr-margin" style={{ marginTop: 28 }} />

          <div className="message-host-section margin-xl-bottom">
            <div className="flex space-between margin-lg-bottom">
              <div>
                <div className="flex middle h6 no-top-margin">
                  <img className="icon margin-md-right" src="https://assets.peerspace.com/message.svg?v=rb" alt="Message Icon" />
                  <span>
                    <FormattedMessage
                      id="booking_request.review_request.message_your_host"
                      defaultMessage="Message your host"
                    />
                  </span>
                </div>
                <div className="help-block no-top-margin text-base">
                  <FormattedMessage
                    id="booking_request.review_request.introduce"
                    defaultMessage="Introduce yourself to {ownerFirstName} and describe what you're planning."
                    values={{
                      ownerFirstName: ownerFirstName
                    }}
                  />
                </div>
              </div>

              <div>
                <img className="avatar-img-sm" src={ownerAvatarUrl} />
              </div>
            </div>

            <div>
              <Field
                name="booking_use_message"
                attributes={{ 'data-testing-id': 'booking-use-message-field' }}
                component={TextAreaField}
                rows={5}
              />
            </div>
          </div>

          <hr style={{ margin: '32px 0' }} />

          <div className="disclaimer-block">
            { (isInstaBookingWithValidAttendees)
              ? <TosCTAdisclaimer location="requestBkgInsta" buttonLabel={ this.props.intl.formatMessage({ id: 'booking_request.review_request.confirm_and_pay', defaultMessage: 'Confirm and Pay' }) } />
              : <TosCTAdisclaimer location="requestBkgNewTab" buttonLabel={ this.props.intl.formatMessage({ id: 'booking_request.review_request.requst_to_book', defaultMessage: 'Request to Book' }) } />
            }
          </div>

          <div
            className="flex space-between column-sm-reverse"
            style={{ gap: '16px' }}
          >
            <Button
              type="button"
              className="btn btn-default classic"
              disabled={isSubmitting}
              onClick={handleClickBack}
            >
              <FormattedMessage
                id="booking_request.review_request.back_button"
                defaultMessage="Back"
              />
            </Button>

            <CheckoutButton
              canExpressCheckout={this.canExpressCheckout}
              buttonProps={{
                category: 'primary',
                type: 'submit',
                isLoading: isSubmitting,
                disabled: isSubmitting,
                attributes: { 'data-testing-id': 'booking-request-submit-button' },
                children: isInstaBookingWithValidAttendees
                  ? <FormattedMessage
                    id="booking_request.review_request.confirm_and_pay"
                    defaultMessage="Confirm and Pay"
                  />
                  : <FormattedMessage
                    id="booking_request.review_request.requst_to_book"
                    defaultMessage="Request to Book"
                  />
              }}
            />

          </div>
        </div>
    );
  }
}

RequestReview.propTypes = {
  spaceRentalContract: PropTypes.object,
  isInstaBookingWithValidAttendees: PropTypes.bool,
  spaceRentalBooking: PropTypes.object,
  isSubmitting: PropTypes.bool,
  selectedPayment: PropTypes.bool,
  paymentInfo: PropTypes.object,
  normalizedContracts: PropTypes.object,
  handleClickBack: PropTypes.func,
  ownerFirstName: PropTypes.string,
  ownerAvatarUrl: PropTypes.string,
  ssoId: PropTypes.string,
  masterContract: PropTypes.object,
  toggleModal: PropTypes.func,
  whichModal: PropTypes.func,
  listingData: PropTypes.object,
  credits: PropTypes.object,
  handleApplyCreditChange: PropTypes.func,
  applyCreditProcessing: PropTypes.bool,
  intl: intlShape,
  dispatch: PropTypes.func,
  cancellationPolicies: PropTypes.array,
  splitPaymentsCountries: PropTypes.object
};

const selector = formValueSelector(BOOKING_REQUEST_FORM);
const mapStateToProps = (state = {}) => {
  const cancellationPolicies = _.get(
    state,
    'env.spaces.cancellation_policies',
    []
  );
  const selectedPaymentOption = selector(state, 'split_payment');
  const guestSocialEventsPolicyAccepted = selector(state, 'guest_social_events_policy_accepted');

  return {
    cancellationPolicies,
    selectedPaymentOption,
    guestSocialEventsPolicyAccepted
  };
};
export default injectIntl(connect(mapStateToProps)(withStripeElements(RequestReview)));
